import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import { Crosshair, Loader2 } from 'lucide-react';

import { addLeadActivity } from '@/app/services/leadService';
import { formatCoordinates, getCurrentCoordinates } from '@/app/lib/geolocation';
import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileInput } from '@/components/ui/MobileInput';
import { MobileTextarea } from '@/components/ui/MobileTextarea';
import { cn } from '@/lib/utils';
import {
    LEAD_STATUS_LABELS,
    type LeadActivityType,
    type LeadStatus,
} from '@/app/types/lead';
import { SELECTABLE_STATUSES, addDaysISO, todayISO } from '../leadUi';

const TYPE_CHIPS: { value: LeadActivityType; label: string }[] = [
    { value: 'call', label: 'Call' },
    { value: 'visit', label: 'Visit' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'note', label: 'Note' },
];

interface Props {
    leadId: number;
    open: boolean;
    onClose: () => void;
    currentStatus: LeadStatus;
    /** Preselects a stage when opened by tapping a status chip. */
    presetStatus?: LeadStatus | null;
}

export function AddActivitySheet({ leadId, open, onClose, currentStatus, presetStatus }: Props) {
    const queryClient = useQueryClient();
    const [activityType, setActivityType] = useState<LeadActivityType>('call');
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState<LeadStatus | ''>('');
    const [lostReason, setLostReason] = useState('');
    const [followUp, setFollowUp] = useState('');
    const [attachLocation, setAttachLocation] = useState(false);
    const [locating, setLocating] = useState(false);

    useEffect(() => {
        if (open) {
            setActivityType(presetStatus ? 'note' : 'call');
            setNotes('');
            setStatus(presetStatus ?? '');
            setLostReason('');
            setFollowUp('');
            setAttachLocation(false);
        }
    }, [open, presetStatus]);

    const mutation = useMutation({
        mutationFn: async () => {
            let geo: string | undefined;
            if (attachLocation) {
                setLocating(true);
                try {
                    const coords = await getCurrentCoordinates();
                    geo = formatCoordinates(coords.lat, coords.lng);
                } catch (error) {
                    // Proof-of-visit is a bonus, not a gate — log the visit anyway.
                    toast.error(
                        error instanceof Error ? error.message : 'Could not attach location'
                    );
                } finally {
                    setLocating(false);
                }
            }

            return addLeadActivity(leadId, {
                activity_type: activityType,
                notes: notes.trim() || undefined,
                status: status || undefined,
                lost_reason: status === 'lost' ? lostReason.trim() || undefined : undefined,
                next_follow_up_date: followUp || undefined,
                geo_location: geo,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['lead-summary'] });
            toast.success('Follow-up logged');
            onClose();
        },
        onError: (error) => {
            const detail = axios.isAxiosError(error) ? error.response?.data?.detail : null;
            toast.error(typeof detail === 'string' ? detail : 'Could not save the follow-up');
        },
    });

    const needsLostReason = status === 'lost' && !lostReason.trim();

    return (
        <MobileBottomSheet open={open} onClose={onClose} title="Log follow-up" heightMode="auto">
            <div className="p-4 space-y-4">
                <div>
                    <p className="mobile-section-header mb-2">What happened?</p>
                    <div className="flex gap-2 flex-wrap">
                        {TYPE_CHIPS.map((chip) => (
                            <button
                                key={chip.value}
                                onClick={() => setActivityType(chip.value)}
                                className={cn(
                                    'px-3 py-2 rounded-full text-sm font-medium',
                                    activityType === chip.value
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-secondary text-muted-foreground'
                                )}
                            >
                                {chip.label}
                            </button>
                        ))}
                    </div>
                </div>

                <MobileTextarea
                    label="Notes"
                    rows={3}
                    placeholder="What did they say?"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />

                <div>
                    <p className="mobile-section-header mb-2">Move to stage (optional)</p>
                    <div className="flex gap-2 flex-wrap">
                        {SELECTABLE_STATUSES.filter((s) => s !== currentStatus).map((s) => (
                            <button
                                key={s}
                                onClick={() => setStatus(status === s ? '' : s)}
                                className={cn(
                                    'px-3 py-2 rounded-full text-sm font-medium',
                                    status === s
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-secondary text-muted-foreground'
                                )}
                            >
                                {LEAD_STATUS_LABELS[s]}
                            </button>
                        ))}
                    </div>
                </div>

                {status === 'lost' && (
                    <div>
                        <MobileInput
                            label="Why lost?"
                            placeholder="Required"
                            value={lostReason}
                            onChange={(e) => setLostReason(e.target.value)}
                        />
                        <div className="flex gap-2 flex-wrap mt-2">
                            {['Price too high', 'Has a supplier', 'Shop closed', 'No response'].map(
                                (reason) => (
                                    <button
                                        key={reason}
                                        onClick={() => setLostReason(reason)}
                                        className="px-3 py-1.5 rounded-full text-xs bg-secondary text-muted-foreground"
                                    >
                                        {reason}
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                )}

                {status !== 'lost' && (
                    <div>
                        <p className="mobile-section-header mb-2">Next follow-up</p>
                        <div className="flex gap-2 mb-2">
                            {[
                                { label: 'Tomorrow', value: addDaysISO(1) },
                                { label: '3 days', value: addDaysISO(3) },
                                { label: '1 week', value: addDaysISO(7) },
                            ].map((opt) => (
                                <button
                                    key={opt.label}
                                    onClick={() => setFollowUp(followUp === opt.value ? '' : opt.value)}
                                    className={cn(
                                        'flex-1 py-2 text-sm rounded-lg font-medium',
                                        followUp === opt.value
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-secondary text-muted-foreground'
                                    )}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <input
                            type="date"
                            min={todayISO()}
                            value={followUp}
                            onChange={(e) => setFollowUp(e.target.value)}
                            className="mobile-input"
                        />
                    </div>
                )}

                <button
                    onClick={() => setAttachLocation(!attachLocation)}
                    className={cn(
                        'w-full flex items-center gap-3 p-3 rounded-xl border transition-colors',
                        attachLocation
                            ? 'border-primary bg-primary/10'
                            : 'border-border bg-card'
                    )}
                >
                    {locating ? (
                        <Loader2 className="h-5 w-5 text-primary animate-spin" />
                    ) : (
                        <Crosshair
                            className={cn(
                                'h-5 w-5',
                                attachLocation ? 'text-primary' : 'text-muted-foreground'
                            )}
                        />
                    )}
                    <div className="flex-1 text-left">
                        <p className="text-sm font-medium">Attach my location</p>
                        <p className="text-xs text-muted-foreground">
                            Records where you were — proof of visit
                        </p>
                    </div>
                </button>

                <MobileButton
                    className="w-full"
                    isLoading={mutation.isPending}
                    disabled={needsLostReason}
                    onClick={() => mutation.mutate()}
                >
                    Save follow-up
                </MobileButton>
            </div>
        </MobileBottomSheet>
    );
}
